import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Grid,
  Stack,
  Typography,
} from '@mui/material'

interface FaqItem {
  question: string
  answer: string
}

const FAQS: readonly FaqItem[] = [
  {
    question: 'What is a WQI score and what does mine mean?',
    answer:
      'Your Water Quality Index score summarizes how your test results compare to EPA health standards. Scores range from 0–100. Higher is safer — a score of 80+ is generally safe, 60–79 warrants monitoring, and below 60 indicates issues that need attention.',
  },
  {
    question: 'My results say "Cautious" — is my water safe to drink right now?',
    answer:
      'Cautious means one or more readings are elevated but below EPA action thresholds. Your water is likely safe for most uses, but we recommend filtering and keeping an eye on any flagged contaminants — especially for infants or immunocompromised individuals.',
  },
  {
    question: 'How often should I test my water?',
    answer:
      'We recommend quarterly testing. Water quality can shift seasonally, after heavy rain, or when your utility makes infrastructure changes. SIPP Pro sends automatic reminders so you never miss a test.',
  },
  {
    question: 'What contaminants does my current filter actually remove?',
    answer:
      "That depends on your filter's NSF certification. Most refrigerator and under-sink filters (NSF/ANSI 42 + 53) reduce lead, chlorine, and cysts. They don't cover everything — your SIPP report shows which of your flagged contaminants fall outside your current filter's range.",
  },
  {
    question: 'How does SIPP know what\'s happening with my utility?',
    answer:
      "We pull live data from the EPA's Safe Drinking Water Information System (SDWIS), cross-referenced with USGS monitoring and community reports. Your utility's violation history, contaminant levels, and compliance status are all sourced from public records.",
  },
  {
    question: "What's the difference between SIPP Pro and the Smart Home Monitor?",
    answer:
      'SIPP Pro is a subscription that covers quarterly test kits, utility monitoring, and filter fulfillment. The Smart Home Monitor is a hardware device that provides continuous real-time water quality sensing at your tap. They work together, but either can be used independently.',
  },
  {
    question: "Does SIPP work if I'm on well water?",
    answer:
      "Yes — and it's especially important if you are. Well water isn't regulated by the EPA and has no utility testing it on your behalf. SIPP provides quarterly test kits, result analysis, and for well users, we strongly recommend the Smart Home Monitor for continuous disinfection detection.",
  },
  {
    question: 'How do I know when to replace my filter?',
    answer:
      "Filter lifespan depends on usage and your water's contaminant load. SIPP Pro tracks this and sends you a replacement reminder — and ships you the right filter — before your current one expires.",
  },
  {
    question:
      "What happens after I sign up for SIPP Pro — what's the first thing I receive?",
    answer:
      "Within a few days you'll receive your first quarterly test kit by mail. Once you complete the test and submit your strip photo, you'll get updated results and a refreshed set of recommendations. Filter fulfillment begins after your first result is processed.",
  },
  {
    question: 'Can I cancel my SIPP Pro subscription at any time?',
    answer:
      'Yes. You can cancel anytime from your account settings with no cancellation fees. Your subscription remains active through the end of the billing period.',
  },
]

const FaqCard = ({ item }: { item: FaqItem }) => (
  <Accordion
    disableGutters
    elevation={0}
    sx={{
      border: '1px solid',
      borderColor: 'divider',
      borderRadius: '8px !important',
      mb: 1,
      '&:before': { display: 'none' },
    }}
  >
    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
      <Typography variant="body2" fontWeight={600}>
        {item.question}
      </Typography>
    </AccordionSummary>
    <AccordionDetails>
      <Typography variant="body2" color="text.secondary">
        {item.answer}
      </Typography>
    </AccordionDetails>
  </Accordion>
)

export const ContactFaqSection = () => {
  const leftColumn = FAQS.slice(0, 5)
  const rightColumn = FAQS.slice(5)

  return (
    <Stack spacing={3} sx={{ width: '100%' }}>
      <Box
        sx={{
          bgcolor: 'primary.light',
          borderRadius: 2,
          p: { xs: 2.5, md: 3 },
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          flexWrap: { xs: 'wrap', md: 'nowrap' },
        }}
      >
        <ChatBubbleOutlineIcon sx={{ color: 'primary.main', fontSize: 28, flexShrink: 0 }} />
        <Stack spacing={0.5} sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body1" fontWeight={600}>
            Have a question about your results?
          </Typography>
          <Typography variant="body2" color="text.secondary">
            We&apos;re here — typically respond within one business day.
          </Typography>
        </Stack>
        <Button
          variant="contained"
          href="mailto:getsipp@sippsafely.com"
          sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}
        >
          Email us
        </Button>
      </Box>

      <Typography variant="h5" fontWeight={700}>
        Frequently asked questions
      </Typography>

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 6 }}>
          {leftColumn.map((item) => (
            <FaqCard key={item.question} item={item} />
          ))}
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          {rightColumn.map((item) => (
            <FaqCard key={item.question} item={item} />
          ))}
        </Grid>
      </Grid>
    </Stack>
  )
}
